import { Expansion } from './expansion.js';
import { ensureExpansion, nextQuest, cellKey, DEVICE_IDS, deviceState, claimReturns, giveOrKeep } from './expansion-rules.js';
import { applyChangesToLists, friendMarks, isMine, markFriends, sessionDiscovery } from './room-mirror.js';
import { holdOp } from './world-sync.js';
import { identity } from './identity.js';
import { friendsChipHTML } from './friends-ui.js';
import { moreSheetHTML, guideSheetHTML } from './explore-ui.js';
// Persistent learning, crafting and exploration sandbox. Reuses the adventure's real player and camera.
import * as THREE from "three";
import { VoxelWorld } from "./voxel.js";
import { B, BLOCKS } from "./blocks.js";
import { Player } from "./player.js";
import { boxFree } from "./physics.js";
import { Pet } from "./pet.js";
import { NPC, Sign, disposeObj } from "./entities.js";
import { gemModel, nameTag } from "./models.js";
import { levelInfo, CATALOG, unlockMet } from "./progression.js";
import { LESSON_LADDER } from "./questions.js";
import { audio } from "./audio.js";
import { haptics } from "./haptics.js";
import { DiscoveryStream, EXPLORE_FOG } from "./discovery-world.js";
import { fogAroundPlayer } from "./fog.js";
import {
  REGION,
  BIOMES,
  biomeAt,
  giantRegion,
  GIANT_SPOT,
  groundHeight,
  homeRegion,
  regionKey,
} from "./discovery-terrain.js";
import { addTownDetails } from "./discovery-decor.js";
import { minimapMarksHTML, exploreMapHTML, friendsHereHTML, mapRegions, MAP_LANDMARKS, MAP_PLACES, MAP_RANGE } from "./explore-map.js";
import {
  STATIONS,
  PALETTE,
  MAX_BLOCKS,
  cleanBlocks,
  validBuildCell,
  protectedCell,
  discoveryReward,
  renewableCell,
  refundStallBlocks,
  rollLessonChest,
  builderGift,
} from "./discovery-state.js";
import { escapeHtml, rng, withName } from "./util.js";
import {
  ITEMS,
  HARVEST,
  amount,
  addItem,
  blockItem,
  selectedItem,
  ensureInventory,
  assignSlot,
  syncSupplies,
  craft,
  claimProject,
  harvestSeconds,
  cleanHarvested,
} from "./crafting.js";
import { inventoryHTML, hotbarHTML } from "./inventory-ui.js";
import { heldItemModel } from "./craft-models.js";
import { Mob } from "./mobs.js";
import { Wildlife } from "./discovery-wildlife.js";

// The note added to a reward toast when full stacks sent some of it to Lost & found.
const lostAndFound = (n) => (n ? ` 📦 ${n} wait${n === 1 ? "s" : ""} in Lost & found until your bag has room.` : "");

export class ExploreMode {
  constructor(game, opts = {}) {
    this.game = game;
    this.group = new THREE.Group();
    game.scene.add(this.group);
    this.world = new VoxelWorld(game.mats);
    this.group.add(this.world.group);
    this.fx = game.fx;
    this.colliders = [];
    this.stats = { jumps: 0, bounces: 0 };
    this.time = 0;
    this.state = "play";
    this.building = false;
    this.hotbarOpen = null;
    this.selected = 0;
    this.stations = [];
    this.entities = [];
    this.forceDoubleJump = true;
    // Visiting a friend keeps your own bag, hearts and stamps but builds in their world.
    this.session = opts.session || null;
    const own = game.save.data.discovery;
    this.data = this.session ? sessionDiscovery(own, this.session.world) : own;
    this.me = identity.data?.playerId || "local";
    this.worldOwner = this.session ? this.session.ownerId : this.me;
    // Who built each friend's block. Your own world learns this from the server each visit, so it never takes
    // space in your save; until then the save's marks ([x,y,z,id,1]) keep friends' blocks from refunding you.
    this.hostOwners = this.session ? {} : friendMarks(own.blocks);
    ensureInventory(game.save.data);
    cleanHarvested(this.data);
    ensureExpansion(this.data);
    // The public table moved to the Craft & Trade stall (1.6): your builds in its space go back in your bag.
    const moved = this.session ? null : refundStallBlocks(own, this.hostOwners, (op) => this.recordOp(op));
    if (moved?.removed)
      game.ui.toast(
        `The Craft & Trade stall moved beside the pond. ${moved.refunded ? `${moved.refunded} block${moved.refunded === 1 ? "" : "s"} went back in your bag.` : "Blocks in its way were tidied up."}${moved.waiting ? ` 📦 ${moved.waiting} more item${moved.waiting === 1 ? " is" : "s are"} safe in Lost & found and will come back when your bag has room.` : ""}`,
        "blue",
        6000,
      );
    else this.claimLostAndFound();
    this.data.removed = this.data.removed.filter((b) =>
      validBuildCell(...b.slice(0, 3)),
    );
    this.loot = [];
    this.mining = null;
    this.wildlife = new Wildlife(this);
    this.enemies = new Map();
    this.data.blocks = cleanBlocks(this.data.blocks);
    if (!this.session) {
      markFriends(this.data.blocks, this.hostOwners);
      this.data.takenDown = (Array.isArray(own.takenDown) ? own.takenDown : []).filter(
        (t) => Array.isArray(t) && t.slice(0, 4).every(Number.isInteger),
      );
    }
    this.data.biomes ||= {};
    this.data.built ||= this.data.blocks.length > 0;
    this.waypoint = null;
    this.moved = this.data.answers > 0;
    this.guideDismissed = this.data.guideDone;
    this.wild = new Map();
    this.lastBiome = "";
  }
  build() {
    this.game.camRig.allowFirstPerson = true;
    this.spec = { spawn: { x: 0.5, y: 41, z: -3.5, yaw: 0 }, killY: 4 };
    this.killY = 4;
    this.world.tint = (x, y, z) => 0.96 + 0.04 * Math.sin(x * 0.35 + z * 0.27);
    this.stream = new DiscoveryStream(
      this.world,
      this.data.blocks,
      (rx, rz, s) => {
        this.wildlife.addRegion(rx, rz);
        this.addEnemy(rx, rz);
        for (const b of [...this.data.harvested, ...this.data.removed])
          if (
            Math.floor(b[0] / REGION) === rx &&
            Math.floor(b[2] / REGION) === rz &&
            this.world.get(...b.slice(0, 3)) === b[3] &&
            !this.data.blocks.some((p) =>
              p.slice(0, 3).every((v, i) => v === b[i]),
            )
          )
            this.world.set(b[0], b[1], b[2], 0);
        if (s) {
          const topic = STATIONS[s.topic],
            biome = biomeAt(s.x, s.z);
          const station = this.addStation({
            ...topic,
            ...s,
            topicId: topic.id,
            name: biome.name + " lesson",
            short: topic.name.split(" ")[0].toUpperCase(),
          });
          this.wild.set(regionKey(rx, rz), station);
        }
      },
      (rx, rz) => {
        this.wildlife.removeRegion(rx, rz);
        this.removeEnemy(rx, rz);
        const key = regionKey(rx, rz),
          s = this.wild.get(key);
        if (s) {
          this.group.remove(s.obj);
          s.tag.material.map.dispose();
          disposeObj(s.obj);
          this.stations.splice(this.stations.indexOf(s), 1);
          this.wild.delete(key);
        }
      },
    );
    this.character = this.game.makePlayerCharacter();
    this.group.add(this.character.root);
    this.player = new Player(this, this.character);
    for (const def of STATIONS) this.addStation(def);
    const savedHearts = this.data.hearts;
    this.goHome();
    this.data.hearts = savedHearts;
    this.player.maxHearts = 5;
    this.player.hearts = this.data.hearts;
    this.player.invuln = 2;
    // Free adoption works for saves made before dogs existed.
    const s = this.game.save.data;
    if (!s.owned.pet.includes("dog")) s.owned.pet.push("dog");
    if (s.equip.pet === "none") s.equip.pet = "dog";
    this.setPet(s.equip.pet);
    this.game.refreshCharacter();
    this.stream.update(0.5, -3.5, true);
    this.decor = addTownDetails(this.group);
    this.trail = new THREE.Group();
    this.group.add(this.trail);
    const trailMat = new THREE.MeshBasicMaterial({
      color: "#fff3a2",
      transparent: true,
      opacity: 0.8,
    });
    const trailGeo = new THREE.OctahedronGeometry(0.13);
    for (let i = 0; i < 14; i++) {
      const dot = new THREE.Mesh(trailGeo, trailMat);
      this.trail.add(dot);
    }
    this.entities.push(
      new NPC(
        this,
        {
          kind: "professor",
          x: -7,
          y: 41,
          z: -8,
          text: "Hi {name}! I’m here if you need me. Follow the sparkles to your next discovery! 🌱",
        },
        90,
      ),
    );
    this.entities.push(
      new Sign(this, { x: 0, y: 41, z: 2, text: `${(this.session?.ownerName || this.game.save.data.name).toUpperCase()}’S BUILD` }),
    );
    this.egg = new THREE.Mesh(
      new THREE.IcosahedronGeometry(0.65, 1),
      new THREE.MeshLambertMaterial({
        color: "#b285ff",
        emissive: "#623399",
        emissiveIntensity: 0.3,
      }),
    );
    this.egg.scale.set(0.85, 1.2, 0.85);
    this.egg.position.set(4, 41.9, -5);
    this.group.add(this.egg);
    this.egg.visible = !this.data.dragonHatched;
    const eggTag = nameTag("DRAGON EGG", {
      color: "#d6afff",
      crown: false,
      scale: 1.8,
    });
    eggTag.position.set(4, 43, -5);
    this.group.add(eggTag);
    this.eggTag = eggTag;
    const previewBox = new THREE.BoxGeometry(1.02, 1.02, 1.02);
    this.preview = new THREE.LineSegments(
      new THREE.EdgesGeometry(previewBox),
      new THREE.LineBasicMaterial({ color: "#b5ff9c", depthTest: true, depthWrite: false }),
    );
    previewBox.dispose();
    this.preview.renderOrder = 10;
    this.preview.visible = false;
    this.group.add(this.preview);
    this.hud = document.createElement("div");
    this.hud.id = "explore-hud";
    this.game.ui.root.appendChild(this.hud);
    this.data.visits++;
    this.game.save.persist();
    this.selectSlot(this.data.inventory.slot);
    const tableTag = nameTag("CRAFT & TRADE", {
      color: "#ffd782",
      crown: false,
      scale: 2.2,
    });
    tableTag.position.set(-9.5, 43.5, -15.5);
    this.group.add(tableTag);
    this.tableTag = tableTag;
    const groveTag = nameTag("WOOD & STONE", {
      color: "#ffe8a6",
      crown: false,
      scale: 2,
    });
    groveTag.position.set(10, 44, -11);
    this.group.add(groveTag);
    this.groveTag = groveTag;
    this.expansion = new Expansion(this);
    this.expansion.build();
    // Builder's gift (1.7): a Builder Workshop stamp earned before the gift existed still brings it, once.
    if (builderGift(this.data)) {
      this.game.save.persist();
      setTimeout(() => this.state !== "disposed" && this.builderGiftToast(), 1200);
    }
    if (this.data.expansion.firstPerson) this.game.camRig.setDistance(0);
    this.refreshHUD();
  }
  setPet(kind) {
    this.pet?.dispose();
    this.pet = new Pet(this, kind);
  }
  goHome() {
    if (!this.player) return;
    this.cancelGather();
    this.expansion?.stopRide();
    this.player.hearts = 5;
    this.data.hearts = 5;
    const s = this.spec.spawn;
    this.player.spawn(new THREE.Vector3(s.x, s.y, s.z), s.yaw);
    this.stream?.update(s.x, s.z, true);
    this.game.camRig.snap(this.player.pos, s.yaw);
    if (this.hud) {
      this.waypoint = null;
      this.refreshHUD();
    }
  }
  playerDied() {
    this.goHome();
    const bed=this.data.expansion.home;
    if(bed){
      this.stream.update(bed[0],bed[2],true);
      const [x,y,z]=bed;
      const exists=this.data.blocks.some(b=>b[3]===B.BED&&Math.hypot(b[0]+.5-x,b[1]-y,b[2]+.5-z)<3);
      const support=[1,2,3].some(dy=>this.world.solidF(x,y-dy,z));
      if(exists&&support&&boxFree(this.world,x-.31,y,z-.31,x+.31,y+1.8,z+.31)){
        this.player.spawn(new THREE.Vector3(...bed));this.game.camRig.snap(this.player.pos);
      }else{this.data.expansion.home=null;this.goHome();}
    }
    this.game.ui.toast(
      "Back home with full hearts. Your items and buildings are safe! 🐾",
      "green",
      4500,
    );
    this.game.save.persist();
  }
  onPlayerHurt() {
    this.cancelGather();
    this.data.hearts = this.player.hearts;
    this.refreshHUD();
    this.game.save.persist();
  }
  swordHit(player) {
    if (!this.strikeTarget) return;
    const mob = this.strikeTarget;
    this.strikeTarget = null;
    if (mob.dead || mob.dying || mob.body.pos.distanceTo(player.pos) > 3.4)
      return;
    const it = selectedItem(this.data);
    if(this.data.expansion.enchant.frost && selectedItem(this.data)?.tool==='sword') mob.stun=3;
    mob.hit(
      new THREE.Vector3(Math.sin(player.facing), 0, Math.cos(player.facing)),
      it?.tool === "sword" ? it.power : 1,
    );
  }
  addEnemy(rx, rz) {
    if (homeRegion(rx, rz)) return;
    const key = regionKey(rx, rz);
    if (this.enemies.has(key)) return;
    // A giant region has a Mossy Giant instead of its slime or zombie: the same place for every player,
    // though each player meets (and beats) their own.
    const giant = giantRegion(rx, rz),
      spot = giant ? GIANT_SPOT : { x: 22.5, z: 7.5 },
      x = rx * REGION + spot.x,
      z = rz * REGION + spot.z;
    const m = new Mob(
      this,
      giant ? "giant" : Math.abs(rx + rz) % 2 ? "slime" : "zombie",
      x,
      groundHeight(Math.floor(x), Math.floor(z)) + 1,
      z,
    );
    if (!giant) m.hp = m.kind === "slime" ? 3 : 6; // a giant keeps its own 10 (GIANT.hp)
    m.maxHp = m.hp;
    m.region = key;
    this.enemies.set(key, m);
  }
  removeEnemy(rx, rz) {
    const key = regionKey(rx, rz),
      m = this.enemies.get(key);
    if (m) {
      m.dispose();
      this.enemies.delete(key);
    }
  }
  onMobDefeated(m, stomped) {
    this.data.defeated[m.region] = this.data.worldTime + 150;
    const s = this.game.save.data;
    s.stats.mobs++;
    if (stomped) s.stats.stomps++;
    if (m.kind === "giant") {
      // The Mossy Giant's bundle: exact items, 15 coins and sometimes a diamond.
      const diamond = Math.random() < 0.4,
        loot = { crystal: 3, iron: 2, ...(diamond ? { diamond: 1 } : {}) },
        at = m.body.pos.clone().add(new THREE.Vector3(0, 2, 0));
      // Whatever a full stack can't take waits in Lost & found, so the bundle is never lost.
      let kept = 0;
      for (const [id, n] of Object.entries(loot)) {
        const got = giveOrKeep(this.data, id, n);
        kept += n - got;
        if (got) this.lootVisual(id, got, at);
      }
      s.coins += 15;
      s.stats.coinsTotal += 15;
      s.stats.giants = (s.stats.giants || 0) + 1;
      this.fx.particles.confetti(at, 90, 2.5);
      audio.play("levelUp");
      haptics.play("levelComplete");
      this.game.ui.toast(
        `🗿 You beat the Mossy Giant! +3 crystal · +2 iron · +15 coins${diamond ? " · +1 diamond ✨" : ""}${lostAndFound(kept)}`,
        "green",
        5000,
      );
    } else {
      const id = m.kind === "slime" ? "crystal" : "iron";
      if (giveOrKeep(this.data, id, 1)) this.lootVisual(id, 1, m.body.pos);
      haptics.play("mobDie");
      this.game.ui.toast(`Monster defeated! +1 ${id}`, "green");
    }
    this.game.save.persist();
    this.refreshHUD();
  }
  spawnLoot(pos, kind) {
    if (kind === "coin") {
      const s = this.game.save.data;
      s.coins++;
      s.stats.coinsTotal++;
      this.refreshHUD();
      this.game.save.persist();
    }
  }
  enemyTarget() {
    const p = this.player.pos,
      fx = Math.sin(this.player.facing),
      fz = Math.cos(this.player.facing);
    return [...this.enemies.values()]
      .filter(
        (m) =>
          !m.dead &&
          !m.dying &&
          m.obj.visible &&
          m.body.pos.distanceTo(p) < 3.4,
      )
      .find((m) => {
        const d = m.body.pos.clone().sub(p);
        if (d.x * fx + d.z * fz <= -0.2) return false;
        const origin = p.clone().add(new THREE.Vector3(0, 1, 0)),
          delta = m.body.pos
            .clone()
            .add(new THREE.Vector3(0, m.body.h / 2, 0))
            .sub(origin),
          dist = delta.length();
        delta.normalize();
        return !this.world.raycast(
          origin.x,
          origin.y,
          origin.z,
          delta.x,
          delta.y,
          delta.z,
          dist,
          (id) => BLOCKS[id].solid,
        );
      });
  }
  updateEnemies(dt) {
    const p = this.player.pos,
      safe = homeRegion(Math.floor(p.x / REGION), Math.floor(p.z / REGION));
    for (const m of this.enemies.values()) {
      if ((this.data.defeated[m.region] || 0) > this.data.worldTime) {
        m.obj.visible = false;
        continue;
      }
      if (this.data.defeated[m.region] || m.dead) {
        delete this.data.defeated[m.region];
        m.hp = m.maxHp;
        m.dead = false;
        m.dying = 0;
        m.stun = 0;
        m.obj.scale.setScalar(1);
        m.body.pos.copy(m.home);
        m.body.vel.set(0, 0, 0);
        if (m.kind === "giant") m.calm();
      }
      m.obj.visible = true;
      if (m.tag) m.tag.visible = m.body.pos.distanceTo(p) < 30;
      if (safe || m.body.pos.distanceTo(p) > 32) {
        m.body.pos.copy(m.home);
        m.obj.position.copy(m.home);
        continue;
      }
      if (
        homeRegion(
          Math.floor(m.body.pos.x / REGION),
          Math.floor(m.body.pos.z / REGION),
        )
      ) {
        m.body.pos.copy(m.home);
        m.body.vel.set(0, 0, 0);
      }
      m.update(dt);
    }
    this.updateGiantBar();
  }
  // The nearest awake Mossy Giant within 18 blocks fills the strength bar. giantBar keeps it for refreshHUD.
  updateGiantBar() {
    const p = this.player.pos;
    let giant = null,
      near = 18;
    for (const m of this.enemies.values()) {
      const d = m.body.pos.distanceTo(p);
      if (m.kind === "giant" && m.obj.visible && !m.dead && !m.dying && d < near) {
        giant = m;
        near = d;
      }
    }
    if (giant && !this.game.giantMet) {
      // Once per game session, the first time a giant comes near.
      this.game.giantMet = true;
      this.game.ui.toast("🗿 A Mossy Giant! Hit it with your sword or arrows, and jump away when it stomps.", "blue", 5500);
    }
    this.giantBar = giant ? Math.max(0, giant.hp) / giant.maxHp : null;
    const bar = this.hud.querySelector(".giant-bar");
    if (!bar) return;
    if (bar.hidden !== !giant) bar.hidden = !giant;
    const fill = bar.querySelector("i"),
      width = `${Math.round((this.giantBar ?? 0) * 100)}%`;
    if (fill.style.width !== width) fill.style.width = width;
  }
  addStation(def) {
    const obj = new THREE.Group();
    obj.position.set(def.x + 0.5, 41, def.z + 0.5);
    const pedestal = new THREE.Mesh(
      new THREE.BoxGeometry(1.4, 0.7, 1.4),
      new THREE.MeshLambertMaterial({ color: def.colour }),
    );
    pedestal.position.y = 0.35;
    pedestal.castShadow = true;
    obj.add(pedestal);
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(1.05, 0.06, 4, 24),
      new THREE.MeshBasicMaterial({ color: def.colour }),
    );
    ring.rotation.x = Math.PI / 2;
    ring.position.y = 0.12;
    obj.add(ring);
    const gem = gemModel(def.colour);
    gem.position.y = 1.5;
    gem.scale.setScalar(1.5);
    obj.add(gem);
    const tag = nameTag(
      def.short ||
        {
          numbers: "NUMBERS",
          words: "WORDS",
          crystals: "PATTERNS",
          farm: "FRACTIONS",
          clock: "TIME",
          builder: "SHAPES",
        }[def.id],
      { color: def.colour, crown: false, scale: 2.1 },
    );
    tag.position.y = 2.7;
    obj.add(tag);
    this.group.add(obj);
    const s = { ...def, obj, gem, tag };
    this.stations.push(s);
    return s;
  }
  guide() {
    const touch = this.game.input.touchMode;
    if (!this.guideDismissed && !this.moved)
      return {
        title: "Let’s take a few steps",
        text: touch
          ? "Move with the circle on the left. Drag the right side to look."
          : "Move with W A S D or arrow keys. Drag to look around.",
        target: STATIONS[0],
      };
    if(this.data.dragonHatched) return nextQuest(this.data, this.game.save.data.name);
    if (
      this.player &&
      !homeRegion(
        Math.floor(this.player.pos.x / REGION),
        Math.floor(this.player.pos.z / REGION),
      )
    ) {
      const near = [...this.wild.values()].sort(
        (a, b) =>
          this.player.pos.distanceTo(a.obj.position) -
          this.player.pos.distanceTo(b.obj.position),
      )[0];
      if (near)
        return {
          title: "A discovery nearby",
          text: "Visit this gem for more building blocks, or keep exploring.",
          target: near,
        };
    }
    if (!this.data.answers)
      return {
        title: "Follow the sparkles to Numbers",
        text: "Walk near the green gem. Press Learn for 3 little puzzles.",
        target: STATIONS[0],
      };
    if (!this.data.built)
      return {
        title: "Build a little home for Scout",
        text: "You earned blocks! Stand by the golden square and press Build.",
        target: { x: 0, z: 6, name: "Your building plot", icon: "🧱" },
      };
    if (!(this.data.gathered.wood >= 3))
      return {
        title: "Gather wood for your workshop",
        text: "Choose the axe in slot 2. Tap a tree trunk in Wood & Stone to chop it.",
        target: { x: 10, z: -20, name: "Wood & Stone grove", icon: "🪵" },
      };
    if (!this.data.crafted.workbench)
      return {
        title: "Make your own crafting table",
        text: "Gather 4 wood and 2 stone. Open Inventory → Craft → Cool things.",
        target: { x: 9, z: -12, name: "Wood & Stone grove", icon: "⛏️" },
      };
    const next = STATIONS.find((s) => !this.data.stamps[s.id]);
    if (next)
      return {
        title: "Your next discovery: " + next.name,
        text: "Each new stamp helps your dragon egg hatch.",
        target: next,
      };
    return {
      title: "A whole world to discover",
      text: "Follow any path out of town. New places keep appearing.",
      target: null,
    };
  }
  refreshHUD() {
    this.hud.classList.toggle("building", this.building);
    const stamps = STATIONS.filter((s) => this.data.stamps[s.id]).length,
      guide = this.guide();
    const it = selectedItem(this.data),
      save = this.game.save.data;
    const open = this.hotbarOpen ?? !this.game.input.touchMode;
    const label = this.session ? `${escapeHtml(this.session.ownerName.toUpperCase())}’S WORLD` : "EXPLORE & CRAFT";
    const social = this.game.mp ? this.game.mp.chipHTML() : friendsChipHTML({ status: "none" });
    this.hud.innerHTML = `<div class="explore-top"><div class="island-card"><small>${label}</small><strong class="biome-name">Home Meadow</strong><span>🪙 ${save.coins} · 💎 ${save.gems} · 📖 ${stamps}/6</span><div class="explore-hearts" aria-label="Health ${this.player.hearts} of 5">${Array.from({ length: 5 }, (_, i) => (i < this.player.hearts ? "♥" : "♡")).join(" ")}</div></div>
      <div class="explore-tools"><button class="btn btn-small btn-blue wide-only" data-act="modeHome" aria-label="Home screen, swap game modes">⌂ Menu</button><button class="btn btn-small btn-purple wide-only" data-act="expansion" data-op="quests">🐉 Quest</button><button class="btn btn-small btn-dark" data-act="journal" aria-label="Map and discoveries">🗺️<span class="wide-only"> Map</span></button><button class="btn btn-small btn-dark wide-only" data-act="exploreHome" aria-label="Return to town">🏡 Town</button>${social}<button class="btn btn-small btn-dark wide-only" data-act="exploreHelp" aria-label="How to play">?</button><button class="btn btn-small btn-dark phone-only" data-act="exploreMore" aria-label="More: quest, town, friends, help, home">☰</button><button class="btn btn-small btn-dark" data-act="pause" aria-label="Pause">⏸</button></div></div>
      <button class="island-map" data-act="journal" aria-label="Open map and choose a place"><small>NEAR YOU · N ↑</small><svg viewBox="-40 -40 80 80" role="img" aria-label="Map centred on you. Gems are learning places. Blue circles are friends."><rect x="-40" y="-40" width="80" height="80" rx="8" fill="#406249"/><g class="map-landmarks"></g><g class="map-player"><circle r="2" fill="white"/><path d="M0 -6 L-2 -2 L2 -2Z" fill="white"/></g></svg><span>You ▲ · Learn ◆ · Friends ●</span></button>
      <div class="explore-objective" data-act="exploreGuide" role="button"><strong>${escapeHtml(guide.title)}</strong><span>${escapeHtml(guide.text)}</span><div class="wayfinder"></div>${!this.guideDismissed ? '<button data-act="dismissGuide" class="guide-skip">I know the controls ✓</button>' : ""}</div>
      <div class="explore-prompt" role="status"></div><div class="gather-progress" role="status"></div>
      <div class="giant-bar"${this.giantBar == null ? " hidden" : ""}><strong>🗿 Mossy Giant</strong><div class="giant-track"><i style="width:${Math.round((this.giantBar ?? 0) * 100)}%"></i></div></div>
      <div class="build-dock survival-dock ${open ? 'slots-open' : 'slots-collapsed'}"><div class="build-actions"><button class="btn btn-small btn-yellow" data-act="inventory">🎒 Inventory <small>I</small></button><strong class="held-name">${it ? it.name : "Empty hand"}${it?.tool === "sword" ? ` · ⚔️ ${it.power}` : ""}</strong><button class="btn btn-small btn-blue hotbar-toggle" data-act="toggleHotbar" aria-controls="explore-slots" aria-expanded="${open}" aria-label="${open ? 'Hide slots' : 'Show slots'} · holding ${it?.name || 'nothing'}">${it?.icon || '✋'} ${open ? 'Hide slots ▾' : 'Show slots ▴'}</button>${this.building ? '<button class="btn btn-small btn-dark" data-act="removeBlock">↶ Remove <small>R</small></button>' : '<button class="btn btn-small btn-dark" data-act="buildMode">🧱 Build</button>'}</div><div id="explore-slots" ${open ? '' : 'hidden'}>${hotbarHTML(this.data)}<div class="build-help">${this.building ? "Tap a face to place · R / Remove returns a block" : it?.tool === "axe" ? "Tap a tree trunk to chop · move to stop" : it?.tool === "pick" ? "Tap a rock or ore to mine · move to stop" : it?.tool === "sword" ? "Tap an animal to hit · choose blocks to build" : it?.id === "food" ? "Press Use / E for 2 hearts & a speed boost" : "Choose an item in Inventory, then a numbered slot"}</div></div></div>`;
    this.updateMap();
    this.game.input.layout.apply();
  }
  toggleHotbar() {
    if (this.state !== 'play' || this.game.paused) return;
    const focused = this.hud.querySelector('[data-act="toggleHotbar"]') === document.activeElement;
    this.hotbarOpen = !(this.hotbarOpen ?? !this.game.input.touchMode);
    this.refreshHUD();
    if (focused) this.hud.querySelector('[data-act="toggleHotbar"]').focus();
  }
  dismissGuide() {
    this.guideDismissed = true;
    this.data.guideDone = true;
    this.moved = true;
    this.game.save.persist();
    this.refreshHUD();
  }
  navigate(id) {
    this.waypoint =
      id === "table"
        ? { x: -10, z: -16, name: "Crafting table", icon: "🛠️" }
        : id === "grove"
          ? { x: 10, z: -15, name: "Wood & Stone grove", icon: "🪵" }
          : id === "home"
            ? { x: 0.5, z: -3.5, name: "Home", icon: "⌂" }
            : this.stations.find((s) => s.id === id) ||
              STATIONS.find((s) => s.id === id);
    this.game.resume();
    this.refreshHUD();
  }
  help() {
    if (this.state !== "play") return;
    this.cancelGather();
    this.game.paused = true;
    this.game.input.reset();
    audio.setDuck(true);
    const touch = this.game.input.touchMode;
    this.game.ui.modal(
      `<div class="panel journal-panel help-panel"><button class="btn btn-small btn-dark journal-back" data-act="resume">← Back</button><small>YOU CAN TAKE YOUR TIME</small><h2>${this.session ? `${escapeHtml(this.session.ownerName)}’s world` : `Your world, ${escapeHtml(this.game.save.data.name)}`}</h2><div class="help-steps"><p><b>1 · Move & look</b>${touch ? "Circle to walk. Drag the right side up, down or sideways to look. Pinch in for eye view, or out to see yourself." : "W A S D or arrows to walk. Drag to look. Scroll or + / − to zoom."} Jump with ${touch ? "the Jump button" : "Space"}.</p><p><b>2 · Learn & earn</b>Walk to a glowing gem and press Learn. Each answer earns oak blocks and coins. Finish three for gems. These buy better gear. Hints and the speaker button can help.</p><p><b>3 · Build & explore</b>Open Inventory. Tap an item, then a numbered slot. Blocks place, axes chop trees, pickaxes mine rocks and swords hunt animals. Move to stop gathering. Remove returns placed blocks. A sword can also break blocks, but the right tool is quicker.</p></div><p>🗺️ Map shows places and friends. 🏡 Town brings you back. ⌂ Menu swaps modes.${touch ? " On a phone, ☰ More holds Quest, Town, Help and Home screen." : ""}<br>Show slots opens your shortcuts; picking one hides them again. Pause → Move controls lets you drag the buttons and Save.<br>🛠️ A crafting table turns supplies into better tools. Learn in any mode to unlock recipes.<br>❤️ Monsters live beyond town. Eat food to heal. Empty hearts bring you home with your items safe.<br>🥚 Six different learning stamps hatch your dragon!<br>👥 Friends: friends’ worlds that are online are listed under Friends playing now, or share your world code. Wave with 😊 (in the 👥 panel on phones), and learn together for Team Stars ⭐.</p><button class="btn btn-green" data-act="resume">Let’s play</button></div>`,
      "modal-journal",
    );
  }
  // Map → Go to a friend: the sparkle trail follows them (updateGuide refreshes it every frame).
  navigateToFriend(id) {
    const f = this.game.mp?.peers.list().find((q) => q.id === String(id));
    if (!f || f.x === null) {
      this.game.ui.toast(f ? `${escapeHtml(f.name)} is still arriving. Try again in a moment!` : "That friend has gone home. Choose another place!", "dark");
      return;
    }
    this.waypoint = { peer: f.id, x: f.x, z: f.z, name: f.name, icon: "🙂" };
    this.game.resume();
    this.refreshHUD();
    this.game.ui.toast(`Follow the sparkles to ${escapeHtml(f.name)}!`, "blue");
  }
  updateMap() {
    if (!this.player || !this.hud) return;
    const p = this.player.pos,
      home = homeRegion(Math.floor(p.x / REGION), Math.floor(p.z / REGION)),
      biome = biomeAt(p.x, p.z);
    const title = this.hud.querySelector(".biome-name");
    if (title) title.textContent = home ? "Home Meadow" : biome.name;
    const map = this.hud.querySelector(".map-landmarks");
    if (map)
      map.innerHTML = minimapMarksHTML({
        p,
        stations: this.stations,
        friends: this.game.mp?.peers.list() || [],
        range: 38,
      });
    this.hud
      .querySelector(".map-player")
      ?.setAttribute(
        "transform",
        `rotate(${(-this.player.facing * 180) / Math.PI})`,
      );
  }
  updateGuide() {
    if (
      !this.moved &&
      this.player.pos.distanceTo(new THREE.Vector3(0.5, 41, -3.5)) > 3
    ) {
      this.moved = true;
      this.refreshHUD();
    }
    // Following a friend: keep the waypoint on them, and drop it quietly once they have left.
    const peer = this.waypoint?.peer;
    if (peer != null) {
      const f = this.game.mp?.peers.list().find((q) => q.id === peer);
      if (f && f.x !== null) Object.assign(this.waypoint, { x: f.x, z: f.z, name: f.name });
      else this.waypoint = null;
    }
    const target =
        this.waypoint || (!this.building ? this.guide().target : null),
      p = this.player.pos;
    this.trail.visible = !!target && !this.building;
    const finder = this.hud.querySelector(".wayfinder");
    if (!target) {
      if (finder) finder.textContent = "";
      return;
    }
    // Places are block corners, so aim for the middle of the block; a friend's position is exact.
    const mid = target.peer != null ? 0 : 0.5,
      dx = target.x + mid - p.x,
      dz = target.z + mid - p.z,
      dist = Math.hypot(dx, dz);
    const yaw = this.game.camRig.yaw,
      angle = Math.atan2(
        -dx * Math.cos(yaw) + dz * Math.sin(yaw),
        dx * Math.sin(yaw) + dz * Math.cos(yaw),
      );
    if (finder)
      finder.innerHTML = `<i style="transform:rotate(${angle}rad)">↑</i><span>${target.icon || "◆"} ${escapeHtml(target.name)} · ${Math.round(dist)} steps</span>`;
    for (let i = 0; i < this.trail.children.length; i++) {
      const d = 1.4 + i * 0.65,
        t = Math.min(1, d / (dist || 1)),
        x = p.x + dx * t,
        z = p.z + dz * t,
        dot = this.trail.children[i];
      dot.visible = d < dist && dist > 3;
      dot.position.set(
        x,
        groundHeight(Math.floor(x), Math.floor(z)) +
          1.25 +
          Math.sin(this.time * 3 - i * 0.6) * 0.08,
        z,
      );
      dot.rotation.y = this.time;
    }
    if (this.waypoint && dist < (this.waypoint.peer != null ? 2.5 : 3)) {
      this.waypoint = null;
    }
  }
  toggleBuild() {
    if (this.state !== "play" || this.game.paused) return;
    if (this.building) {
      this.selectSlot(
        this.data.inventory.hotbar.findIndex(
          (id) => ITEMS[id]?.kind === "tool",
        ),
      );
      return;
    }
    let slot = this.data.inventory.hotbar.findIndex(
      (id) => ITEMS[id]?.kind === "block" && amount(this.data, id),
    );
    if (slot < 0 && amount(this.data, "planks")) {
      slot = 3;
      assignSlot(this.data, "planks", slot);
    }
    if (slot < 0) return this.openInventory();
    this.selectSlot(slot);
  }
  selectBlock(i) {
    const p = PALETTE[i];
    if (!p || !amount(this.data, p.item)) return;
    assignSlot(this.data, p.item, 3);
    this.selectSlot(3);
  }
  selectSlot(i, fromHotbar = false) {
    if (
      !Number.isInteger(i) ||
      i < 0 ||
      i > 7 ||
      this.state !== "play" ||
      this.game.paused
    )
      return;
    this.cancelGather();
    this.data.inventory.slot = i;
    if (fromHotbar && this.game.input.touchMode) this.hotbarOpen = false;
    const it = selectedItem(this.data);
    if (it?.tool === "sword" && amount(this.data, it.id))
      this.game.save.data.equip.sword = it.tier;
    this.building = it?.kind === "block";
    this.selected = PALETTE.findIndex((p) => p.item === it?.id);
    this.character.setHeldObject(
      it && amount(this.data, it.id)
        ? heldItemModel(it.id, this.game.mats)
        : null,
      this.building,
    );
    this.preview.visible = false;
    this.game.save.persist();
    this.refreshHUD();
  }
  syncHeldItem() {
    const it = selectedItem(this.data);
    this.character.setHeldObject(
      it && amount(this.data, it.id) ? heldItemModel(it.id, this.game.mats) : null,
      it?.kind === "block",
    );
  }
  nearBench() {
    const p = this.player.pos;
    if (p.distanceTo(new THREE.Vector3(-9.5, 41.5, -15.5)) < 4.5) return true;
    return this.data.blocks.some(
      (b) =>
        b[3] === B.CRAFTING_TABLE &&
        p.distanceTo(new THREE.Vector3(b[0] + 0.5, b[1] + 0.5, b[2] + 0.5)) <
          4.5,
    );
  }
  openInventory(tab = "bag", group = this.recipeGroup || "build") {
    if (this.state !== "play") return;
    if (!["bag", "craft", "shop", "projects"].includes(tab)) return;
    this.cancelGather();
    this.claimLostAndFound();
    this.panelTab = tab;
    this.recipeGroup = group;
    this.game.paused = true;
    this.game.input.reset();
    audio.setDuck(true);
    this.game.ui.modal(inventoryHTML(this, tab, group), "modal-inventory");
  }
  // Lost & found items come back once their stacks have room (own world only).
  claimLostAndFound() {
    if (this.session) return;
    const { got, left } = claimReturns(this.data);
    if (!got) return;
    this.game.save.persist();
    this.game.ui.toast(`📦 Lost & found: ${got} item${got === 1 ? "" : "s"} went back in your bag.${left ? ` ${left} more will follow when there is room.` : ""}`, "blue", 4500);
  }
  chooseInventoryItem(id) {
    if (this.panelTab !== "bag" || !this.game.paused || !amount(this.data, id))
      return;
    this.bagChoice = id;
    this.openInventory("bag");
  }
  chooseInventorySlot(i) {
    if (
      this.panelTab !== "bag" ||
      !this.game.paused ||
      !Number.isInteger(i) ||
      i < 0 ||
      i > 7
    )
      return;
    if (this.bagChoice) assignSlot(this.data, this.bagChoice, i);
    this.data.inventory.slot = i;
    this.bagChoice = null;
    const it = selectedItem(this.data);
    if (it?.tool === "sword" && amount(this.data, it.id))
      this.game.save.data.equip.sword = it.tier;
    this.building = it?.kind === "block";
    this.selected = PALETTE.findIndex((p) => p.item === it?.id);
    this.character.setHeldObject(
      it && amount(this.data, it.id)
        ? heldItemModel(it.id, this.game.mats)
        : null,
      this.building,
    );
    this.game.save.persist();
    this.refreshHUD();
    this.openInventory("bag");
  }
  craftItem(id, shop = false) {
    if (
      this.state !== "play" ||
      !this.game.paused ||
      this.panelTab !== (shop ? "shop" : "craft") ||
      (shop && !this.nearBench())
    )
      return false;
    const result = craft(this.game.save.data, id, this.nearBench(), shop);
    if (!result.ok) {
      this.game.ui.toast(result.missing[0], "blue");
      return false;
    }
    this.game.save.persist();
    this.syncHeldItem();
    this.game.refreshCharacter();
    audio.play("buy");
    haptics.play("craft");
    this.game.ui.toast(
      `+${result.n} ${ITEMS[result.item].name} · in your Inventory!`,
      "green",
    );
    this.refreshHUD();
    this.openInventory(this.panelTab);
    return true;
  }
  claimProject(id) {
    if (
      this.state !== "play" ||
      !this.game.paused ||
      this.panelTab !== "projects" ||
      !claimProject(this.game.save.data, id)
    )
      return false;
    this.game.save.persist();
    audio.play("unlock");
    this.refreshHUD();
    this.openInventory("projects");
    return true;
  }
  useSelected() {
    if (this.game.paused || this.state !== "play") return;
    if(this.expansion?.useHeld())return;
    const it = selectedItem(this.data);
    if (it?.id === "food" && amount(this.data, "food")) {
      this.data.inventory.items.food--;
      this.syncHeldItem();
      this.player.heal(2);
      this.data.hearts = this.player.hearts;
      this.player.power.speed = 30;
      this.game.save.persist();
      this.game.ui.toast(
        "Snack power! +2 hearts and faster exploring for 30 seconds.",
        "green",
      );
      audio.play("powerup");
      this.refreshHUD();
    }
  }
  ray(tap) {
    const ray = new THREE.Raycaster();
    ray.setFromCamera(
      new THREE.Vector2(
        tap ? (tap.x / innerWidth) * 2 - 1 : 0,
        tap ? 1 - (tap.y / innerHeight) * 2 : (this.game.camRig.firstPerson ? 0 : 0.08),
      ),
      this.game.camera,
    );
    return ray;
  }
  targetCell(remove = false, tap) {
    const g = this.game,
      ray = new THREE.Raycaster();
    ray.setFromCamera(
      new THREE.Vector2(
        tap ? (tap.x / window.innerWidth) * 2 - 1 : 0,
        tap ? 1 - (tap.y / window.innerHeight) * 2 : (this.game.camRig.firstPerson ? 0 : 0.08),
      ),
      g.camera,
    );
    const { origin: o, direction: d } = ray.ray;
    const hit = this.world.raycast(
      o.x,
      o.y,
      o.z,
      d.x,
      d.y,
      d.z,
      g.camRig.distCur + 12,
      (id) => !!id && !BLOCKS[id].liquid && !BLOCKS[id].cross,
    );
    if (!hit) return null;
    const cell = remove
      ? [hit.x, hit.y, hit.z]
      : [hit.x + hit.nx, hit.y + hit.ny, hit.z + hit.nz];
    const [x, y, z] = cell;
    if (this.player.pos.distanceTo(new THREE.Vector3(x + 0.5, y, z + 0.5)) > 9)
      return null;
    return cell;
  }
  canPlace(cell) {
    if (!cell || !validBuildCell(...cell)) return false;
    const [x, y, z] = cell,
      p = this.player;
    if (p.pos.distanceTo(new THREE.Vector3(x + 0.5, y, z + 0.5)) > 9)
      return false;
    if (this.world.get(x, y, z) || this.blockCount() >= MAX_BLOCKS)
      return false;
    return !this.cellTouchesPlayer(cell);
  }
  cellTouchesPlayer(cell) {
    if (!cell) return false;
    const [x, y, z] = cell, p = this.player;
    return (
      p.pos.x + p.hw > x &&
      p.pos.x - p.hw < x + 1 &&
      p.pos.z + p.hw > z &&
      p.pos.z - p.hw < z + 1 &&
      p.pos.y + p.h > y &&
      p.pos.y < y + 1
    );
  }
  placeBlock(cell = this.targetCell()) {
    if (!this.building || this.state !== "play" || this.game.paused)
      return false;
    if (!this.canPlace(cell)) {
      if (!this.buildTipTime || this.time - this.buildTipTime > 3) {
        this.buildTipTime = this.time || 0.01;
        this.game.ui.toast(
          this.blockCount() < MAX_BLOCKS
            ? "Choose an empty face nearby. Leave the spawn, learning gems and public table clear."
            : this.data.blocks.length < MAX_BLOCKS
              ? "Lots of your changes are waiting to be shared. Go online, then you can build more."
              : "Your build collection is full. Remove an old block to make room.",
          "blue",
        );
      }
      return false;
    }
    const it = selectedItem(this.data);
    if (it?.kind !== "block" || !amount(this.data, it.id)) {
      this.game.ui.toast(
        "That stack is empty. Craft more or learn for oak planks! 🧠",
        "blue",
      );
      return false;
    }
    const [x, y, z] = cell;
    if (this.session && DEVICE_IDS.has(it.block)) {
      this.game.ui.toast("Chests, doors and machines go in your own world. Try a building block here!", "blue");
      return false;
    }
    if(DEVICE_IDS.has(it.block)&&this.data.blocks.filter(b=>DEVICE_IDS.has(b[3])).length>=500){this.game.ui.toast('Your world has 500 working blocks. Remove a few to make room for new machines.','blue');return false;}
    if(it.block===B.CHEST && this.data.blocks.filter(b=>b[3]===B.CHEST).length>=8){this.game.ui.toast('You can have 8 storage chests. Each keeps 64 kinds of item.','blue');return false;}
    if(it.block===B.DOOR && (amount(this.data,it.id)<2||this.world.get(x,y+1,z)||this.blockCount()+2>MAX_BLOCKS||!validBuildCell(x,y+1,z)))return false;
    this.world.set(x, y, z, it.block, true);
    this.world.rebuild();
    this.data.blocks.push([x, y, z, it.block]);
    this.data.inventory.items[it.id]--;
    this.markBuilt([x, y, z], it);
    this.expansion?.afterPlace([x,y,z,it.block]);
    if(it.block===B.DOOR){this.world.set(x,y+1,z,it.block,true);this.data.blocks.push([x,y+1,z,it.block]);this.data.inventory.items[it.id]--;this.markBuilt([x,y+1,z],it);this.expansion.afterPlace([x,y+1,z,it.block]);}
    syncSupplies(this.data);
    this.character.placeGesture();
    this.lootVisual(
      it.id,
      1,
      new THREE.Vector3(x + 0.5, y + 0.5, z + 0.5),
      true,
    );
    if (!amount(this.data, it.id)) this.character.setHeldObject(null);
    this.data.built = true;
    this.data.guideDone = true;
    this.guideDismissed = true;
    this.game.save.persist();
    audio.play("pop");
    haptics.play("place");
    this.refreshHUD();
    return true;
  }
  removeBlock(cell = this.targetCell(true)) {
    if (!this.building || this.state !== "play" || this.game.paused || !cell)
      return false;
    if (
      this.player.pos.distanceTo(
        new THREE.Vector3(cell[0] + 0.5, cell[1], cell[2] + 0.5),
      ) > 9
    )
      return false;
    const i = this.data.blocks.findIndex((b) =>
      b.slice(0, 3).every((v, j) => v === cell[j]),
    );
    if (i < 0) return false;
    const mine = this.isMine(cell);
    if (!mine && !this.isHost) {
      this.game.ui.toast("That’s a friend’s block. Only they can take it down.", "blue");
      return false;
    }
    const removedBlock=this.data.blocks[i];
    if(!this.expansion.beforeRemove(removedBlock))return false;
    const item = blockItem(this.data.blocks[i][3]);
    if (mine && !addItem(this.data, item, 1)) return false;
    const key = cellKey(cell), prevOwner = this.owners[key] || null;
    this.fx.particles.dust(
      new THREE.Vector3(cell[0] + 0.5, cell[1] + 0.5, cell[2] + 0.5),
      9,
      BLOCKS[this.data.blocks[i][3]].color,
    );
    this.character.placeGesture();
    this.world.set(...cell, 0, true);
    this.world.rebuild();
    this.data.blocks.splice(i, 1);
    delete this.owners[key];
    this.expansion.afterRemove(removedBlock);
    this.recordOp({ k: "remove", x: cell[0], y: cell[1], z: cell[2], id: removedBlock[3], item: mine ? item : null, n: mine ? 1 : 0, prevOwner });
    const held = selectedItem(this.data);
    if (held?.id === item)
      this.character.setHeldObject(heldItemModel(item, this.game.mats), true);
    this.game.save.persist();
    audio.play("pop");
    this.refreshHUD();
    return true;
  }
  canHarvest(cell) {
    if (!cell || protectedCell(cell[0], cell[2])) return false;
    const id = this.world.get(...cell),
      own = this.data.blocks.some((b) =>
        b.slice(0, 3).every((v, i) => v === cell[i]),
      );
    if (!HARVEST[id] && !own) return false;
    if (own && !this.isHost && !this.isMine(cell)) return false;
    const [x, y, z] = cell;
    return (
      this.player.pos.distanceTo(
        new THREE.Vector3(x + 0.5, y + 0.5, z + 0.5),
      ) <= 7 && y >= 9
    );
  }
  renewableCell([x, y, z]) {
    return renewableCell(x, y, z);
  }
  cancelGather() {
    this.mining = null;
    if (this.preview) this.preview.visible = false;
    if (this.expansion?.cracks) this.expansion.cracks.visible = false;
    if (this.hud) {
      const el = this.hud.querySelector(".gather-progress");
      if (el) el.innerHTML = "";
    }
  }
  beginGather(cell) {
    if (
      this.state !== "play" ||
      this.game.paused ||
      this.building ||
      !this.canHarvest(cell)
    )
      return false;
    if (this.mining) {
      this.cancelGather();
      return false;
    }
    const ownBlock=this.data.blocks.find(b=>cellKey(b)===cellKey(cell));
    if(ownBlock&&!this.expansion.beforeRemove(ownBlock))return false;
    const id = ownBlock?.[3] || this.world.get(...cell),
      it = selectedItem(this.data),
      own = this.data.blocks.some((b) =>
        b.slice(0, 3).every((v, i) => v === cell[i]),
      );
    const seconds = harvestSeconds(id, it?.id, own);
    if (!Number.isFinite(seconds)) {
      this.game.ui.toast(
        `This ore needs a ${HARVEST[id].rank === 1 ? "stone" : "iron"} pickaxe or better. Craft one at the table!`,
        "blue",
      );
      return false;
    }
    this.mining = {
      cell: cell.slice(),
      id,
      worldId:this.world.get(...cell),
      seconds: seconds/(this.data.expansion.enchant.fortune&&it?.tool==='pick'?1.4:1),
      elapsed: 0,
      item: it?.id,
      own,
      mine: own && this.isMine(cell),
    };
    const p = this.player.pos;
    this.player.facing = Math.atan2(cell[0] + 0.5 - p.x, cell[2] + 0.5 - p.z);
    return true;
  }
  updateGather(dt, input) {
    const m = this.mining;
    if (!m) return;
    if (
      this.game.paused ||
      this.state !== "play" ||
      !this.canHarvest(m.cell) ||
      this.world.get(...m.cell) !== m.worldId ||
      selectedItem(this.data)?.id !== m.item ||
      Math.hypot(input.moveX, input.moveY) > 0.1 ||
      Math.abs(input.camDX) + Math.abs(input.camDY) > 3 ||
      input.wheel ||
      input.cameraTouches?.size > 1
    ) {
      this.cancelGather();
      return;
    }
    m.elapsed += dt;
    if (this.player.swingT < 0) this.player.swing();
    const progress = Math.min(1, m.elapsed / m.seconds),
      h = m.own ? { item: blockItem(m.id), n: m.mine ? 1 : 0, tool: "pick" } : HARVEST[m.id];
    const el = this.hud.querySelector(".gather-progress");
    el.innerHTML = `<strong>${h.tool === "axe" ? "Chopping" : "Mining"} ${ITEMS[h.item].name.toLowerCase()} · ${Math.max(0, m.seconds - m.elapsed).toFixed(1)}s</strong><div><i style="width:${progress * 100}%"></i></div><button data-act="stopGather">Stop · or walk away</button>`;
    if (Math.floor(m.elapsed * 5) !== m.dustTick) {
      m.dustTick = Math.floor(m.elapsed * 5);
      this.fx.particles.dust(
        new THREE.Vector3(m.cell[0] + 0.5, m.cell[1] + 0.6, m.cell[2] + 0.5),
        2,
        BLOCKS[m.id].color,
      );
    }
    this.preview.scale.setScalar(1 + Math.sin(m.elapsed * 40) * 0.015);
    if (progress < 1) return;
    const records = this.renewableCell(m.cell)
      ? this.data.harvested
      : this.data.removed;
    if (
      (!m.own &&
        records.length >= (records === this.data.harvested ? 1024 : 2048)) ||
      (h.n && !addItem(this.data, h.item, h.n))
    ) {
      this.game.ui.toast(
        "Use some supplies or explore while this grove regrows.",
        "blue",
      );
      this.cancelGather();
      return;
    }
    const [x, y, z] = m.cell;
    this.world.set(x, y, z, 0, true);
    this.world.rebuild();
    const bonus = !m.own && this.data.expansion.enchant.fortune && selectedItem(this.data)?.tool === 'pick' && addItem(this.data, h.item, 1) ? 1 : 0;
    const gained = { item: h.n ? h.item : null, n: h.n + bonus };
    if (m.own) {
      const i = this.data.blocks.findIndex(
        (b) => b[0] === x && b[1] === y && b[2] === z,
      );
      const removedBlock=this.data.blocks[i];
      const key = cellKey(m.cell), prevOwner = this.owners[key] || null;
      this.data.blocks.splice(i, 1);
      delete this.owners[key];
      this.expansion.afterRemove(removedBlock);
      this.recordOp({ k: "remove", x, y, z, id: m.id, ...gained, prevOwner });
    } else if (records === this.data.harvested) {
      records.push([x, y, z, m.id, this.data.worldTime + 240]);
      this.recordOp({ k: "harvest", x, y, z, id: m.id, secs: 240, ...gained });
    } else {
      if (!records.some((b) => b[0] === x && b[1] === y && b[2] === z))
        records.push([x, y, z, m.id]);
      this.recordOp({ k: "mine", x, y, z, id: m.id, ...gained });
    }
    audio.play("pop");
    haptics.play("gather");
    if (h.n) {
      this.data.gathered[h.item] = (this.data.gathered[h.item] || 0) + h.n;
      this.lootVisual(h.item, h.n, new THREE.Vector3(x + 0.5, y + 0.5, z + 0.5));
      this.game.ui.toast(
        `+${h.n} ${ITEMS[h.item].name} → Inventory`,
        "green",
        1800,
      );
    } else this.game.ui.toast("Tidied a friend’s block. It goes back to the world, not your bag.", "blue", 2500);
    this.cancelGather();
    this.preview.scale.setScalar(1);
    this.game.save.persist();
    this.refreshHUD();
  }
  regrow() {
    if (this.regrowTick === Math.floor(this.data.worldTime)) return;
    this.regrowTick = Math.floor(this.data.worldTime);
    let changed = false;
    this.data.harvested = this.data.harvested.filter((b) => {
      if (b[4] > this.data.worldTime) return true;
      const [x, y, z, id] = b,
        p = this.player.pos;
      if (
        Math.abs(p.x - x - 0.5) < 1 &&
        Math.abs(p.z - z - 0.5) < 1 &&
        p.y < y + 1 &&
        p.y + 2 > y
      ) {
        b[4] = this.data.worldTime + 5;
        return true;
      }
      if (
        this.stream.loaded.has(
          regionKey(Math.floor(x / REGION), Math.floor(z / REGION)),
        ) &&
        !this.world.get(x, y, z)
      ) {
        this.world.set(x, y, z, id, true);
        changed = true;
      }
      return false;
    });
    if (changed) this.world.rebuild();
  }
  lootVisual(id, n, pos, placing = false) {
    const obj = heldItemModel(id, this.game.mats);
    if (!obj) return;
    obj.scale.setScalar(0.6);
    this.character.hand.updateWorldMatrix(true, false);
    const hand = new THREE.Vector3();
    this.character.hand.getWorldPosition(hand);
    obj.position.copy(placing ? hand : pos);
    this.group.add(obj);
    this.loot.push({
      obj,
      from: obj.position.clone(),
      to: placing ? pos.clone() : null,
      t: 0,
      life: placing ? 0.2 : 0.7,
      placing,
    });
  }
  updateLoot(dt) {
    for (let i = this.loot.length - 1; i >= 0; i--) {
      const v = this.loot[i];
      v.t += dt;
      const t = Math.min(1, v.t / v.life),
        target =
          v.to || this.player.pos.clone().add(new THREE.Vector3(0, 1, 0));
      v.obj.position.lerpVectors(v.from, target, t);
      v.obj.position.y += Math.sin(t * Math.PI) * 0.5;
      v.obj.rotation.y += dt * 5;
      if (t === 1) {
        this.group.remove(v.obj);
        disposeObj(v.obj);
        this.loot.splice(i, 1);
      }
    }
  }
  async learn(id, { joined = false } = {}) {
    const station = this.stations.find((s) => s.id === id);
    if (
      !station ||
      this.state !== "play" ||
      this.game.paused ||
      this.player.pos.distanceTo(station.obj.position) > (joined ? 10 : 4.5)
    )
      return;
    this.game.mp?.lessonStart(station);
    this.cancelGather();
    this.state = "quiz";
    this.player.frozen = true;
    this.preview.visible = false;
    this.game.input.reset();
    this.game.input.showTouchControls(false);
    const waiting = this.topicRewardsWaiting();
    const result = await this.game.quiz.run({
      title: `${station.icon} ${station.name}`,
      sub: "Take your time. Hints help you learn!",
      needed: 3,
      weights: station.topics,
      tables: [2, 3, 4, 5, 8, 10],
      allowLater: true,
      selfPaced: true,
      // A warm-up, a question at your level, then a 🔥 challenge one level up.
      ladder: LESSON_LADDER,
    });
    if (this.state === "disposed") return;
    const family = station.topicId || id,
      r = discoveryReward(this.data, family, result),
      s = this.game.save.data;
    // The lesson chest rolls with the game's RNG. Anything a full stack can't take waits in Lost & found.
    const chest = rollLessonChest(family, result, () => rng.next());
    let kept = 0;
    if (chest) for (const [item, n] of Object.entries(chest.items)) kept += n - giveOrKeep(this.data, item, n);
    this.game.mp?.lessonDone(station, result);
    const fromLevel = levelInfo(s.xp).level;
    s.coins += r.coins;
    s.gems += r.gems;
    s.xp += r.xp;
    const toLevel = levelInfo(s.xp).level;
    for (let lv = fromLevel + 1; lv <= toLevel; lv++) s.coins += 20 + lv * 5;
    if (toLevel > fromLevel)
      this.game.ui.toast(
        `⭐ Level ${toLevel}! Your learning is making you stronger.`,
        "green",
        5000,
      );
    s.stats.coinsTotal += r.coins;
    s.stats.gemsTotal += r.gems;
    if (r.count) haptics.play("reward");
    // A won 🔥 challenge gets its own line; the one wrapper lets the line break inside the toast's flex row.
    const won = r.challengeGem ? "<br>🔥 Challenge won! +1 gem" : "";
    if (r.hatch) {
      if (!s.owned.pet.includes("dragon")) s.owned.pet.push("dragon");
      s.equip.pet = "dragon";
      this.setPet("dragon");
      this.game.refreshCharacter();
      this.egg.visible = false;
      this.eggTag.visible = false;
      this.fx.particles.confetti(this.egg.position, 90, 1.5);
      audio.play("unlock");
      this.game.ui.toast(
        `<div>🐉 You hatched EMBER! Your learning brought a dragon to life!${won}</div>`,
        "green",
        6500,
      );
    } else if (r.count) {
      const gems = r.gems - r.challengeGem;
      this.game.ui.toast(
        `<div>${r.newStamp ? "📖 New discovery! " : "Great exploring! "}+${r.count * 8} oak planks · +${r.coins} coins${gems ? ` · +${gems} gem${gems === 1 ? "" : "s"}` : ""}${won}</div>`,
        "green",
        4000,
      );
      this.fx.particles.confetti(
        station.obj.position.clone().add(new THREE.Vector3(0, 1, 0)),
        40,
        1,
      );
    }
    this.lessonExtras(station, r, result, chest, kept, waiting);
    this.state = "play";
    this.player.frozen = false;
    // Let Sai look around again before a nearby monster can land a hit.
    this.player.invuln = Math.max(this.player.invuln, 3);
    for (const mob of this.enemies.values())
      if (mob.body.pos.distanceTo(this.player.pos) < 10) mob.stun = 3;
    this.syncHeldItem();
    this.game.input.reset();
    this.game.input.showTouchControls(true);
    this.game.save.persist();
    this.refreshHUD();
    this.game.checkBadges();
  }
  // Locker items earned by reaching top topic levels (the Crystal Blade) that are still locked.
  topicRewardsWaiting() {
    const s = this.game.save.data;
    return Object.entries(CATALOG).flatMap(([cat, list]) =>
      list.filter((it) => it.unlock?.startsWith("topics:") && !s.owned[cat]?.includes(it.id) && !unlockMet(s, it.unlock)),
    );
  }
  // The rest of a lesson's rewards follow the main toast (which carries the challenge gem): the chest at 0.7 s,
  // with any brain level-up or Locker unlock under it, then the Builder's gift once the main toast has gone.
  // The toast area holds three at once and a badge can arrive with the main toast, so fewer pop-ups means none
  // is pushed out early. `kept` counts chest items waiting in Lost & found because a stack was full.
  lessonExtras(station, r, result, chest, kept, waiting) {
    const s = this.game.save.data,
      gem = station.obj.position.clone().add(new THREE.Vector3(0, 1.2, 0)),
      later = (ms, step) => setTimeout(() => this.state !== "disposed" && step(), ms),
      ups = [...new Set(result.topicUps || [])].map((t) => this.game.topicName(t)),
      unlocked = waiting.filter((it) => unlockMet(s, it.unlock)),
      brain = [
        ...(ups.length ? [`🧠 Brain level up in ${ups.join(" & ")}! You’re ready for trickier questions.`] : []),
        ...unlocked.map((it) => `🎁 ${withName(it.name, escapeHtml(s.name))} unlocked in your Locker!`),
      ],
      cheer = () => {
        audio.play(unlocked.length ? "unlock" : "levelUp");
        haptics.play("levelUp");
      };
    // One wrapper, so each line breaks inside the toast's flex row.
    const toast = (lines, colour, ms) => this.game.ui.toast(`<div>${lines.join("<br>")}</div>`, colour, ms);
    if (chest)
      later(700, () => {
        const items = Object.entries(chest.items),
          head = { common: "🎁 Lesson chest:", rare: "🎁✨ Rare chest!", epic: "🎁💎 EPIC chest!" }[chest.tier];
        items.forEach(([item, n], i) => this.lootVisual(item, n, gem.clone().add(new THREE.Vector3((i - (items.length - 1) / 2) * 0.6, 0, 0))));
        if (chest.tier !== "common")
          this.fx.worldUI.float(
            chest.tier === "epic" ? "🎁 EPIC CHEST!" : "🎁 RARE CHEST!",
            gem.clone().add(new THREE.Vector3(0, 1, 0)),
            chest.tier === "epic" ? "ft-gold ft-huge" : "ft-purple ft-big",
            { life: 1.8, rise: 90 },
          );
        if (chest.tier === "epic") this.fx.particles.confetti(gem, 70, 1.3);
        audio.play("chest");
        haptics.play("chest");
        toast(
          [
            `${head} ${items.map(([item, n]) => `+${n} ${ITEMS[item].name.toLowerCase()}`).join(" · ")}`,
            ...(kept ? [lostAndFound(kept).trim()] : []),
            ...(result.perfect ? ["🌟 Perfect lesson! Your chest is one tier better."] : []),
            ...brain,
          ],
          { common: "green", rare: "purple", epic: "badge" }[chest.tier],
          brain.length ? 5500 : 4500,
        );
        if (brain.length) later(500, cheer);
      });
    else if (brain.length)
      later(700, () => {
        cheer();
        toast(brain, "purple", 5000);
      });
    if (r.gift) later(4100, () => this.builderGiftToast());
  }
  builderGiftToast() {
    audio.play("unlock");
    haptics.play("reward");
    const waiting = this.data.expansion?.returns?.arrow ? " Arrows that don’t fit wait in 📦 Lost & found." : "";
    this.game.ui.toast(`🏹 Builder’s gift: a bow and 12 arrows! Put the bow in a slot, then press Shoot.${waiting}`, "blue", 6500);
  }
  journal() {
    if (this.state !== "play") return;
    this.cancelGather();
    this.game.paused = true;
    this.game.input.reset();
    audio.setDuck(true);
    const d = this.data,
      near = [...this.wild.values()].sort(
        (a, b) =>
          this.player.pos.distanceTo(a.obj.position) -
          this.player.pos.distanceTo(b.obj.position),
      )[0],
      p = this.player.pos,
      you = { x: p.x, z: p.z, facing: this.player.facing },
      friends = this.game.mp?.peers.list() || [];
    // A drawn map first (friends by name, tap anything to set the sparkle trail), then the full list.
    const map = exploreMapHTML({
      player: you,
      range: MAP_RANGE,
      regions: mapRegions(p.x, p.z, MAP_RANGE),
      stations: this.stations.map((s) => ({ id: s.id, name: s.name, icon: s.icon, colour: s.colour, x: s.x, z: s.z, stamped: !!d.stamps[s.id] })),
      landmarks: MAP_LANDMARKS,
      places: MAP_PLACES,
      friends,
      home: { x: 0.5, z: -3.5 },
      waypoint: this.waypoint || (!this.building ? this.guide().target : null),
    });
    this.game.ui.modal(
      `<div class="panel journal-panel"><button class="btn btn-small btn-dark journal-back" data-act="resume">← Back</button><small>YOUR MAP & DISCOVERIES</small><h2>Where shall we go?</h2>${map}<p class="map-hint">Tap a place or a friend. Follow the sparkles to get there.</p><p class="map-legend">You ▲ · Learn ◆ · Friends ●</p>${friendsHereHTML({ player: you, friends, range: MAP_RANGE })}<div class="journal-buttons"><button class="btn btn-yellow" data-act="findTable">🛠️ Crafting table</button><button class="btn btn-dark" data-act="navigate" data-id="grove">🪵 Wood & Stone grove</button></div><div class="journal-grid">${STATIONS.map((s) => `<button data-act="navigate" data-id="${s.id}" style="--station-colour:${s.colour}" class="journal-entry"><span>${s.icon}</span><strong>${s.name}</strong><small>${d.stamps[s.id] ? "✓ Stamp collected" : "3 questions → 24 blocks"}</small><p>${escapeHtml(s.tip)}</p><b>Show me the way →</b></button>`).join("")}</div><div class="journal-goal">${d.dragonHatched ? "🐉 Ember is your friend! Try building a cosy dragon house." : `🥚 Dragon egg: ${STATIONS.filter((s) => d.stamps[s.id]).length} of 6 stamps. Collect one from every kind of gem.`}</div><div class="biome-collection">${BIOMES.map((b) => `<span class="${d.biomes[b.id] ? "found" : ""}">${b.icon} ${b.name}${d.biomes[b.id] ? " ✓" : ""}</span>`).join("")}</div><p>The world keeps growing as you walk. Build beside the paths!<br>Build idea: a wall 4 blocks wide and 3 blocks high. How many blocks?</p><div class="journal-buttons">${near ? `<button class="btn btn-blue" data-act="navigate" data-id="${near.id}">Explore beyond town →</button>` : ""}<button class="btn btn-dark" data-act="navigate" data-id="home">Show the way home</button><button class="btn btn-green" data-act="resume">Back to exploring</button></div></div>`,
      "modal-journal",
    );
  }
  // Explore fogs by flat distance from the player, so zooming out or a wide screen never hides what is close by
  // and never shows the edge of the loaded world (see EXPLORE_FOG and fog.js).
  updateFog() {
    const fog = this.game.scene.fog;
    if (!fog) return;
    fogAroundPlayer(this.player.pos.x, this.player.pos.z);
    fog.near = EXPLORE_FOG.near;
    fog.far = EXPLORE_FOG.far;
  }
  update(dt, input) {
    this.time += dt;
    if (this.state !== "play") return;
    this.updateEnemies(dt);
    this.data.worldTime += dt;
    this.wildlife.update(dt);
    this.updateLoot(dt);
    this.regrow();
    const region = regionKey(
      Math.floor(this.player.pos.x / REGION),
      Math.floor(this.player.pos.z / REGION),
    );
    this.stream.update(this.player.pos.x, this.player.pos.z);
    if (this.guideRegion !== region) {
      this.guideRegion = region;
      this.refreshHUD();
    }
    if(!this.expansion.drive(dt,input))this.player.update(dt, input, this.game.camRig.yaw);
    this.decor.update(this.time, this.player.pos);
    this.updateGuide();
    if (Math.floor(this.time * 5) !== this.mapTick) {
      this.mapTick = Math.floor(this.time * 5);
      this.updateMap();
    }
    const biome = biomeAt(this.player.pos.x, this.player.pos.z);
    const wildNow = !homeRegion(
      Math.floor(this.player.pos.x / REGION),
      Math.floor(this.player.pos.z / REGION),
    );
    if (wildNow && !this.warnedWild) {
      this.warnedWild = true;
      this.game.ui.toast(
        "Wilderness! Watch your hearts. Hit monsters, eat food, or tap Town to get home.",
        "blue",
        5000,
      );
    }
    if (this.lastBiome !== biome.id) {
      this.lastBiome = biome.id;
      if (!this.data.biomes[biome.id]) {
        this.data.biomes[biome.id] = true;
        this.game.save.persist();
        if (biome.id !== "meadow")
          this.game.ui.toast(`${biome.icon} You found ${biome.name}!`, "green");
      }
    }

    this.pet.obj.visible = true;
    if (this.waveTime > this.time)
      this.character.update(dt, { grounded: true, wave: true });
    else if (this.danceTime > this.time)
      this.character.update(dt, { grounded: true, celebrate: true });
    for (const e of this.entities) e.update(dt);
    this.pet.update(dt);
    this.expansion.update(dt,input);
    if(this.game.paused)return;
    this.game.camRig.update(dt, input, this.player.pos, this.world, { forceThirdPerson: !!this.expansion.mount });
    this.updateFog();
    if (this.game.camRig.firstPerson) this.player.facing = this.game.camRig.yaw;
    this.expansion.afterCamera(dt, input);
    this.eggTag.visible = !this.data.dragonHatched;
    this.egg.rotation.y += dt * 0.5;
    this.egg.position.y = 41.9 + Math.sin(this.time * 1.4) * 0.12;
    let near = null;
    for (const s of this.stations) {
      s.tag.visible = this.player.pos.distanceTo(s.obj.position) < 30;
      s.gem.rotation.y += dt;
      s.gem.position.y = 1.5 + Math.sin(this.time * 2 + s.x) * 0.12;
      const dist = this.player.pos.distanceTo(s.obj.position);
      if (dist < 4.5 && (!near || dist < near.dist)) near = { s, dist };
    }
    const prompt = this.hud.querySelector(".explore-prompt");
    for (let i = 0; i < 8; i++)
      if (input.wasPressed(String(i + 1))) this.selectSlot(i);
    const it = selectedItem(this.data),
      tap = input.taps.find((t) => t.touch) || input.taps[0];
    const enemy = !this.building ? this.enemyTarget() : null;
    const ray = this.ray(tap),
      animal = !this.building ? this.wildlife.target(ray) : null;
    const cell = this.targetCell(true, tap),
      harvest = cell && this.canHarvest(cell);
    const riding=this.expansion.mount||this.expansion.rail;
    // Bow aim help adds 🎯 (a no-break space keeps it on one line of the round button).
    const heldAction={bow:this.expansion.aim?'SHOOT\u00a0🎯':'SHOOT',shield:'BLOCK',egg:'HATCH'}[it?.id];
    const deviceContext=(!this.building&&!riding&&(enemy||heldAction))?null:this.expansion.context(cell);
    const table = this.nearBench();
    if (table && near?.dist > 2.6) near = null;
    const context = this.building
      ? ""
      : enemy
        ? enemy.kind === "giant"
          ? `🗿 Mossy Giant · ${enemy.hp} strength`
          : `⚔️ ${enemy.kind === "slime" ? "Slime" : "Zombie"} · ${enemy.hp} strength`
        : near
          ? `${near.s.icon} Learn at ${near.s.name}`
          : table
            ? "🛠️ Craft & trade"
            : animal
              ? `${animal.kind[0].toUpperCase() + animal.kind.slice(1)} · ${animal.hp} strength`
              : harvest
                ? `${ITEMS[blockItem(this.world.get(...cell)) || HARVEST[this.world.get(...cell)]?.item]?.name || "Block"} · tap to gather`
                : "";
    const act = enemy
      ? null
      : near
        ? "learnStation"
        : table
          ? "inventory"
          : null;
    const text = deviceContext ? `<button class="btn btn-green" data-act="expansion" data-op="interact">${deviceContext.label} <small>Use / E</small></button>` : context
      ? act
        ? `<button class="btn btn-green" data-act="${act}" data-tab="craft" data-id="${near?.s.id || ""}">${context} <small>E / tap</small></button>`
        : `<span>${context}</span>`
      : "";
    const shown = text || (!this.building && this.game.mp ? this.game.mp.prompt(this.player.pos) : "");
    if (prompt.innerHTML !== shown) prompt.innerHTML = shown;
    if (this.building) {
      const target = this.targetCell(false, tap);
      this.preview.visible = !!target && !this.cellTouchesPlayer(target) && amount(this.data, it?.id) > 0;
      if (target) {
        this.preview.position.set(
          target[0] + 0.5,
          target[1] + 0.5,
          target[2] + 0.5,
        );
        this.preview.material.color.set(
          this.canPlace(target) && amount(this.data, it?.id) > 0
            ? "#b5ff9c"
            : "#ff8585",
        );
      }
      this.game.input.setActionLabel(deviceContext ? (this.expansion.mount ? "LAND" : this.expansion.rail ? "LEAVE" : "USE") : "PLACE", deviceContext ? "✋" : "🧱");
      if(input.actionPressed&&deviceContext)this.expansion.interact(deviceContext);
      else if (input.wasPressed("r") || input.taps.some((t) => t.button === 2))
        this.removeBlock();
      else if (input.actionPressed || input.taps.some((t) => t.touch))
        this.placeBlock(target);
    } else {
      // Idle gathering hints belong in the action label, never over Sai's head.
      this.preview.visible = !!this.mining;
      if (this.mining) {
        this.preview.position.set(...this.mining.cell.map(v => v + .5));
        this.preview.material.color.set("#ffe09c");
      }
      const label = this.expansion.mount ? "LAND" : this.expansion.rail ? "LEAVE" : heldAction || (deviceContext ? "USE" : enemy
        ? "HIT"
        : near
          ? "LEARN"
          : table
            ? "CRAFT"
            : animal
              ? (it?.id === "lead" ? "ADOPT" : it?.id === "shears" && animal.kind === "sheep" ? "SHEAR" : "HIT")
              : harvest
                ? "GATHER"
                : it?.id === "food"
                  ? "USE"
                  : it?.tool ? "SWING" : "LOOK");
      this.game.input.setActionLabel(
        label,
        near
          ? "🧠"
          : table
            ? "🛠️"
            : animal
              ? "⚔️"
              : harvest
                ? it?.icon || "✋"
                : it?.icon || "👀",
      );
      if (input.actionPressed || input.taps.some((t) => t.touch)) {
        if(this.expansion.mount||this.expansion.rail)this.expansion.interact(deviceContext);
        else if(['bow','shield','egg'].includes(it?.id))this.expansion.useHeld();
        else if (enemy) {
          this.cancelGather();
          if (this.player.swing()) this.strikeTarget = enemy;
        } else if(deviceContext)this.expansion.interact(deviceContext);
        else if(this.expansion.useHeld()){}
        else if (near) this.learn(near.s.id);
        else if (table) this.openInventory("craft");
        else if (animal) {if(!this.expansion.animalAction(animal))this.wildlife.hit(animal);}
        else if(this.expansion.farmAction()){}
        else if (harvest) this.beginGather(cell);
        else if (it?.id === "food") this.useSelected();
        else if (it?.tool) this.player.swing();
      }
      this.updateGather(dt, input);
    }
    if (input.wasPressed("i")) this.openInventory();
    if (input.wasPressed("b")) this.toggleBuild();
    if (input.wasPressed("h")) this.goHome();
  }
  // ---------- shared world (WorldSync host) ----------
  get isHost() {
    return this.me === this.worldOwner;
  }
  get owners() {
    return this.session ? this.data.owners : this.hostOwners;
  }
  lists() {
    const d = this.data;
    return { blocks: d.blocks, removed: d.removed, harvested: d.harvested, owners: this.owners, devices: d.expansion.devices };
  }
  worldTime() {
    return this.data.worldTime;
  }
  isMine(cell) {
    return isMine(this.lists(), cell, this.me, this.worldOwner);
  }
  markBuilt(cell, it) {
    if (!this.isHost) this.owners[cellKey(cell)] = this.me;
    this.recordOp({ k: "place", x: cell[0], y: cell[1], z: cell[2], id: it.block, item: it.id, n: 1 });
  }
  // Blocks taken down while the edit queue was full keep their place in the save until the room has them.
  blockCount() {
    return this.data.blocks.length + (this.session ? 0 : this.data.takenDown.length);
  }
  recordOp(op) {
    if (this.game.mp) return this.game.mp.record(op);
    // Offline in your own world: keep edits so they reach the server next time.
    if (!this.session && this.game.save.data.playerId) holdOp(this.data, op);
  }
  applyChanges(changes, why) {
    const d = this.data, W = this.world;
    const done = applyChangesToLists(this.lists(), changes, {
      worldTime: d.worldTime,
      worldOwner: this.worldOwner,
      deviceState: (key, cell) => deviceState(d, cell.slice(0, 3)),
    });
    const loaded = (c) => this.stream.loaded.has(regionKey(Math.floor(c[0] / REGION), Math.floor(c[2] / REGION)));
    let dirty = false;
    const set = (c, id) => {
      W.set(c[0], c[1], c[2], id, true);
      dirty = true;
      if (this.mining && cellKey(this.mining.cell) === cellKey(c)) this.cancelGather();
    };
    // Snapshots re-send friends' blocks so ownership is known again; only redraw real changes.
    for (const c of done.place || []) if (loaded(c) && W.get(c[0], c[1], c[2]) !== c[3]) set(c, c[3]);
    for (const c of done.clear || []) if (loaded(c) && W.get(c[0], c[1], c[2])) set(c, 0);
    for (const c of [...(done.mine || []), ...(done.harvest || [])]) if (loaded(c) && W.get(c[0], c[1], c[2]) === c[3]) set(c, 0);
    for (const c of [...(done.unmine || []), ...(done.regrow || [])]) if (loaded(c) && !W.get(c[0], c[1], c[2])) set(c, c[3]);
    if (dirty) W.rebuild();
    if (!this.session && done.place?.length) markFriends(d.blocks, this.hostOwners);
    if (done.place?.length || done.clear?.length || done.device?.length) this.expansion?.syncDevices();
    if (!this.session && why !== "rollback") this.persistSoon();
    return done;
  }
  adjustItem(item, delta) {
    if (!item || !delta || !ITEMS[item]) return;
    if (delta > 0) addItem(this.data, item, delta);
    else this.data.inventory.items[item] = Math.max(0, amount(this.data, item) + delta);
    syncSupplies(this.data);
    this.syncHeldItem();
    this.refreshHUD();
  }
  rejected(op, reason) {
    if (this.rejectToastAt > this.time - 3) return;
    this.rejectToastAt = this.time;
    const owner = this.session?.ownerName || "the owner";
    this.game.ui.toast(
      {
        occupied: "Someone built there first! Your block is back in your bag.",
        conflict: "A few changes from while you were away didn’t fit. Your items are back in your bag.",
        owner: "That’s a friend’s block. Only they can take it down.",
        cap: "You’ve built lots here! Take a few blocks down to build more.",
        full: "This world is full of builds. Take some down to make room.",
        device: `Only ${escapeHtml(owner)} can use that here.`,
        protected: "Leave the spawn, learning gems and table clear.",
        slow: "Whoa, speedy builder! Slow down a little.",
      }[reason] || "That didn’t work here, so it’s back how it was.",
      "blue",
      3500,
    );
  }
  persist() {
    this.game.save.persist();
  }
  persistSoon() {
    clearTimeout(this.persistTimer);
    this.persistTimer = setTimeout(() => this.state !== "disposed" && this.game.save.persist(), 1500);
  }
  // ☰ More (phones): the toolbar buttons that don't fit beside Map, Friends and Pause.
  moreSheet() {
    if (this.state !== "play") return;
    this.cancelGather();
    this.game.paused = true;
    this.game.input.reset();
    audio.setDuck(true);
    this.game.ui.modal(
      moreSheetHTML({ friends: this.game.mp?.count || 0, guest: this.session?.ownerName || "" }),
      "modal-journal modal-more",
    );
  }
  // Tapping the guide card shows the whole next step, with its trail and the controls tip.
  guideSheet() {
    if (this.state !== "play") return;
    this.cancelGather();
    this.game.paused = true;
    this.game.input.reset();
    audio.setDuck(true);
    this.game.ui.modal(
      guideSheetHTML(this.guide(), { dismissable: !this.guideDismissed }),
      "modal-journal modal-guide",
    );
  }
  guideTrail() {
    const target = this.guide().target;
    if (target) this.waypoint = target;
    this.game.resume();
    this.refreshHUD();
  }
  friendsPanel() {
    if (this.game.mp) return this.game.mp.openPanel();
    if (this.state !== "play") return;
    this.cancelGather();
    this.game.paused = true;
    this.game.input.reset();
    audio.setDuck(true);
    const signedIn = identity.loggedIn && identity.tokenValid();
    const again = identity.loggedIn && !signedIn;
    this.game.ui.modal(
      `<div class="panel journal-panel friends-panel"><button class="btn btn-small btn-dark journal-back" data-act="resume">← Back</button><small>FRIENDS</small><h2>${signedIn ? "You’re offline" : "Play with friends"}</h2><p>${signedIn ? "Your builds are saved here. They’ll be shared when you’re back online." : again ? "Tell us who’s playing again to share your world. Your builds are safe." : "Make your player card to share your world and visit friends. It only takes a moment."}</p><div class="journal-buttons">${signedIn ? "" : `<button class="btn btn-green" data-act="whoStart">👥 ${again ? "Sign in again" : "Make my player card"}</button>`}<button class="btn btn-dark" data-act="resume">Back to exploring</button></div></div>`,
      "modal-journal modal-friends",
    );
  }
  dispose() {
    this.state = "disposed";
    clearTimeout(this.persistTimer);
    if (this.game.quiz.open) this.game.quiz.cancel?.();
    this.hud?.remove();
    this.expansion?.dispose();
    this.pet?.dispose();
    this.wildlife.dispose();
    for (const m of this.enemies.values()) m.dispose();
    this.enemies.clear();
    for (const v of this.loot) {
      this.group.remove(v.obj);
      disposeObj(v.obj);
    }
    this.loot = [];
    this.tableTag?.material.map.dispose();
    this.groveTag?.material.map.dispose();
    for (const e of this.entities) e.dispose();
    if (this.character) {
      this.group.remove(this.character.root);
      this.character.dispose();
    }
    for (const s of this.stations) s.tag.material.map.dispose();
    this.eggTag.material.map.dispose();
    this.decor.dispose();
    this.world.dispose();
    this.group.remove(this.world.group);
    disposeObj(this.group);
    this.game.scene.remove(this.group);
    this.game.save.persist();
  }
}
